// Demand-test page (owner growth policy, .seo/truth.md, 2026-11-01): the owner
// confirmed bobbleheads are fine to publish. The studio has quoted a sculpted
// resin bobblehead before (June 2026), but that figure is not approved for the
// site, so every price and turnaround here is "quoted per project". One table
// feeds the answer, the options list, the FAQs and the planner's cost note.
const quoted = 'Quoted per project'

const bobbleheadTerms = {
  quoted,
  deskSize: 'about 6–7 inch',
  // Template gift bobbleheads on Walmart and gift sites, SERP 2026-11-01.
  templateGiftPrice: 'under $100',
}

const customBobbleheads = {
  path: '/services/custom-figurines/bobbleheads/',
  reviewedDate: '2026-11-01',
  reviewedDateLabel: 'November 1, 2026',
  title: 'Custom & Personalized Bobbleheads from Photos | PrintAWorld',
  description:
    'Custom bobbleheads of people, couples, mascots and pets, sculpted from your photos, 3D printed in resin and hand-painted in New York. Quoted per project.',
  answer: `PrintAWorld makes custom bobbleheads in New York from your photos: one-off and small-run bobbleheads of a person, a couple, a team or company mascot, or a pet. We sculpt the head and body in 3D, send you the model to approve, then 3D print it in resin, paint it by hand and mount the head on a spring so it bobbles. Each bobblehead is ${quoted.toLowerCase()}, and bulk runs for teams and brands are priced per piece. We ship anywhere in the U.S.`,
}

const options = [
  {
    name: 'One-off bobblehead',
    size: 'About 6–7 inch',
    price: quoted,
    detail:
      'One person, pet or mascot, sculpted from your photos. The 3D model and the first painted figure are quoted together.',
  },
  {
    name: 'Couple or wedding',
    size: 'Two figures, one base',
    price: quoted,
    detail:
      'A bride and groom, an anniversary, two founders. Both figures are sculpted and posed together.',
  },
  {
    name: 'Bigger bobblehead',
    size: '8–12 inch and up',
    price: quoted,
    detail:
      'More room for a likeness, an outfit and props. Size moves the price more than anything else.',
  },
  {
    name: 'Team or brand run',
    size: '2 to 100+ pieces',
    price: 'Priced per piece',
    detail:
      'One sculpt, many copies: a team set, an event giveaway, a client gift. The 3D model is paid once and each quantity is priced per piece.',
  },
]

const choices = [
  {
    title: 'A template gift is fine',
    body: `Gift sites sell bobbleheads for ${bobbleheadTerms.templateGiftPrice} by putting a sculpted head on a ready-made body. If you want a quick, funny gift, a stock outfit is fine and the date is next week, buy one of those.`,
  },
  {
    title: 'Get one sculpted',
    body: 'When it has to look like the person from head to toe, wear a specific outfit, hold their props, pose two people together, or be a mascot or pet no stock body fits. Also when a team or brand needs the same figure many times over.',
  },
]

const subjects = [
  {
    title: 'Yourself or a colleague',
    body: 'A bobblehead of yourself, a boss, a retiring colleague or a groom-to-be, from a few clear photos of the face and the outfit.',
  },
  {
    title: 'A couple or a wedding',
    body: 'A bride and groom on one base for the cake or the head table, or an anniversary gift. Order early: the 3D model needs your approval before we print.',
  },
  {
    title: 'A team or company mascot',
    body: 'The mascot as a desk figure, a giveaway or a trade-show piece. A logo or a single front view is enough; we design the sides and back.',
  },
  {
    title: 'A pet',
    body: 'Your dog or cat with an oversized head, sculpted from your photos.',
    link: ['Pet figurines', '/services/custom-figurines/pets/'],
  },
  {
    title: 'A group or a set',
    body: 'A bridal party, a sports team, a family or a founding team, as matching bobbleheads or several figures on one base.',
  },
  {
    title: 'Corporate gifts in bulk',
    body: 'One sculpt of your mascot, founder or brand character, copied for clients, staff or an event. Every run starts with one painted sample you approve.',
  },
]

const steps = [
  'Send photos: the face from the front and the side, a full-length shot of the outfit, and any props.',
  'We sculpt the head and body in 3D, with the oversized head that makes it a bobblehead.',
  'You approve the 3D model, or tell us what to change, before anything is printed.',
  'We 3D print it in resin and paint it by hand: skin, hair, outfit and logos.',
  'The head goes on a spring neck, the figure on its base, with a name plate if you want one.',
]

const faqs = [
  {
    question: 'How much does a custom bobblehead cost?',
    answer: `${quoted}. We price two parts: sculpting the 3D model of your person, couple, mascot or pet, and printing and hand-painting the figure. Size, a realistic likeness, outfits and props move the price. For team or brand runs, the model is paid once and each quantity is priced per piece. Send photos and the size you want for a written quote.`,
  },
  {
    question: 'Can you make a custom bobblehead from a photo?',
    answer:
      'Yes. Send a close photo of the face from the front, one from the side and a full-length photo of the outfit. Phone photos are fine if they are sharp and in daylight. We sculpt from your photos and send you the 3D model to approve before printing.',
  },
  {
    question: 'Can I get a bobblehead of myself?',
    answer:
      'Yes. A bobblehead of yourself, a boss, a parent or a friend is the most common request. Tell us the outfit, the pose and any props, like a golf club, a guitar or a laptop, and we sculpt them in.',
  },
  {
    question: 'Do you make couple and wedding bobbleheads?',
    answer:
      'Yes. We sculpt both people and pose them together on one base, as a cake topper, a head-table piece or an anniversary gift. Send photos of each person and of the dress and suit, and give yourself time for the 3D model approval before the date.',
  },
  {
    question: 'Can you make mascot or corporate bobbleheads in bulk?',
    answer:
      'Yes. Send the mascot artwork or photos of the person and your brand colors. We sculpt one, you approve a painted sample, then we make the run. Runs are priced per piece for each quantity you ask about, so ask for several.',
  },
  {
    question: 'Can you make a bobblehead of my dog or cat?',
    answer:
      'Yes. Send the face from the front and side and the whole body standing. We exaggerate the head like any bobblehead and paint the coat and markings by hand. For a realistic figure without the bobble, see our pet figurines.',
  },
  {
    question: 'How long does a custom bobblehead take?',
    answer:
      'Timing is quoted per project with your price, because it depends on the sculpt and how many you need. Tell us the date you need it in hand and we will tell you whether it fits. The 3D model needs your approval before printing, so order early for weddings, retirements and holidays.',
  },
  {
    question: 'Why do some custom bobbleheads cost $30 online?',
    answer: `Gift sites keep bobbleheads ${bobbleheadTerms.templateGiftPrice} by sculpting only the head and putting it on a ready-made body. That works for a quick gift. Ours are sculpted from head to toe for your person, couple, mascot or pet, approved by you in 3D and painted by hand, so they are quoted per project.`,
  },
  {
    question: 'Are your bobbleheads 3D printed?',
    answer:
      'Yes. We sculpt in 3D, print in resin and paint by hand. Only the head moves, on a spring; the body is one fixed pose. We don’t make articulated action figures.',
  },
  {
    question: 'Do you ship?',
    answer:
      'Yes, anywhere in the United States. Pickup from our New York studio is available by appointment.',
  },
]

module.exports = {
  customBobbleheads,
  bobbleheadTerms,
  options,
  choices,
  subjects,
  steps,
  faqs,
}
